// Copyright (C) Unite Digital LLC - All Rights Reserved.
// Unauthorized copying of this file, via any medium is strictly prohibited.
// Proprietary and confidential.
// Developed by Unite Digital LLC.

import {
    IdVerification,
    FormDocumentUploadType,
    IdCheckStatus,
    InternalDocumentTypeIds,
    TemplateUploadType,
    Assignees,
    DocumentStatus,
} from '@interfaces/document.interfaces';

export const setTemplateUploadTypeList = (templateUploadFormData: TemplateUploadType[]) => {
    const fallback: Array<TemplateUploadType> = [
        {
            uploadTypeId: FormDocumentUploadType.Predefined_Form,
            uploadTypeDescription: 'Predefined Form',
            dataLoadFlag: true,
            hint: '',
            value: 'formDocumentUploadType.Predefined_Form',
        },
        {
            uploadTypeId: FormDocumentUploadType.Uploaded_During_Deal,
            uploadTypeDescription: 'Upload During Deal',
            dataLoadFlag: false,
            hint: '',
            value: 'formDocumentUploadType.Uploaded_During_Deal',
        },
        {
            uploadTypeId: FormDocumentUploadType.Print_From_PDF,
            uploadTypeDescription: 'Print From Pdf',
            dataLoadFlag: false,
            hint: '',
            value: 'formDocumentUploadType.Print_From_Impact_Form',
        },
        {
            uploadTypeId: FormDocumentUploadType.Print_From_Impact_Form,
            uploadTypeDescription: 'Print From Impact Printer',
            dataLoadFlag: false,
            hint: '',
            value: 'formDocumentUploadType.Print_From_PDF',
        },
        {
            uploadTypeId: FormDocumentUploadType.Manually_Filled_During_Deal,
            uploadTypeDescription: 'Manually Filled During the Deal',
            dataLoadFlag: false,
            hint: '',
            value: 'formDocumentUploadType.Manually_Filled_During_Deal',
        },
        {
            uploadTypeId: FormDocumentUploadType.Dealer_Uploaded_with_Predefine_Signatures,
            uploadTypeDescription: 'Dealer Uploaded with Predefined Signatures',
            dataLoadFlag: false,
            hint: '',
            value: 'formDocumentUploadType.Dealer_Uploaded_with_Predefine_Signatures',
        },
        {
            uploadTypeId: FormDocumentUploadType.Consumer_Documents,
            uploadTypeDescription: 'Consumer Documents',
            dataLoadFlag: false,
            hint: '',
            value: 'formDocumentUploadType.Consumer_Documents',
        },
    ];

    if (!templateUploadFormData || templateUploadFormData === undefined || templateUploadFormData === null) {
        return filterActiveUploadTypes(fallback);
    }

    const enrichedTemplateUploadFormData = templateUploadFormData.map((uploadType) => {
        uploadType.value = fallback.find((x) => x.uploadTypeId === uploadType.uploadTypeId).value;
        return uploadType;
    });

    return filterActiveUploadTypes(enrichedTemplateUploadFormData);
};

export const filterActiveUploadTypes = (templateUploadFormData: Array<TemplateUploadType>) => {
    //Remove Unused Upload Types Manually_Filled_During_Deal.
    templateUploadFormData = templateUploadFormData.filter((x) => x.uploadTypeId !== FormDocumentUploadType.Dealer_Uploaded_with_Predefine_Signatures);
    return templateUploadFormData;
};

export const isIdentification = (internalDocumentTypeId: InternalDocumentTypeIds) => {
    return isDriversLicense(internalDocumentTypeId) || isExtendedId(internalDocumentTypeId);
};

export const isBuyerIdentification = (internalDocumentTypeId: InternalDocumentTypeIds) => {
    return isBuyerDriversLicense(internalDocumentTypeId) || isBuyerPassport(internalDocumentTypeId) || isBuyerPassportCard(internalDocumentTypeId);
};

export const isCoBuyerIdentification = (internalDocumentTypeId: InternalDocumentTypeIds) => {
    return isCoBuyerDriversLicense(internalDocumentTypeId) || isCoBuyerPassport(internalDocumentTypeId);
};

export const isExtendedId = (internalDocumentTypeId: InternalDocumentTypeIds) => {
    return isPassport(internalDocumentTypeId) || isPassportCard(internalDocumentTypeId) || isInternational_Id_Dl(internalDocumentTypeId);
};

export const isPassport = (internalDocumentTypeId: InternalDocumentTypeIds) => {
    return isBuyerPassport(internalDocumentTypeId) || isCoBuyerPassport(internalDocumentTypeId);
};

export const isBuyerPassport = (internalDocumentTypeId: InternalDocumentTypeIds) => {
    return internalDocumentTypeId === InternalDocumentTypeIds.BuyerPassportBooklet;
};

export const isCoBuyerPassport = (internalDocumentTypeId: InternalDocumentTypeIds) => {
    return internalDocumentTypeId === InternalDocumentTypeIds.CoBuyerPassportBooklet;
};

export const isPassportCard = (internalDocumentTypeId: InternalDocumentTypeIds) => {
    return isBuyerPassportCard(internalDocumentTypeId) || isCoBuyerPassportCard(internalDocumentTypeId);
};

export const isBuyerPassportCard = (internalDocumentTypeId: InternalDocumentTypeIds) => {
    return internalDocumentTypeId === InternalDocumentTypeIds.BuyerPassportCard;
};

export const isCoBuyerPassportCard = (internalDocumentTypeId: InternalDocumentTypeIds) => {
    return internalDocumentTypeId === InternalDocumentTypeIds.CoBuyerPassportCard;
};

export const isInternational_Id_Dl = (internalDocumentTypeId: InternalDocumentTypeIds) => {
    return isBuyerInternational_Id_Dl(internalDocumentTypeId) || isCoBuyerInternational_Id_Dl(internalDocumentTypeId);
};

export const isBuyerInternational_Id_Dl = (internalDocumentTypeId: InternalDocumentTypeIds) => {
    return internalDocumentTypeId === InternalDocumentTypeIds.BuyerInternational_Id_Dl;
};

export const isCoBuyerInternational_Id_Dl = (internalDocumentTypeId: InternalDocumentTypeIds) => {
    return internalDocumentTypeId === InternalDocumentTypeIds.CobuyerInternational_Id_Dl;
};

export const isDriversLicense = (internalDocumentTypeId: InternalDocumentTypeIds) => {
    return (
        internalDocumentTypeId === InternalDocumentTypeIds.BuyerDriversLicense ||
        internalDocumentTypeId === InternalDocumentTypeIds.CoBuyerDriversLicense ||
        isRemoteDriversLicense(internalDocumentTypeId)
    );
};

export const isRemoteDriversLicense = (internalDocumentTypeId: InternalDocumentTypeIds) => {
    return (
        internalDocumentTypeId === InternalDocumentTypeIds.BuyerDriversLicenseRemoteUpload ||
        internalDocumentTypeId === InternalDocumentTypeIds.CoBuyerDriversLicenseRemoteUpload
    );
};

export const isBuyerDriversLicense = (internalDocumentTypeId: InternalDocumentTypeIds) => {
    return (
        internalDocumentTypeId === InternalDocumentTypeIds.BuyerDriversLicense ||
        internalDocumentTypeId === InternalDocumentTypeIds.BuyerDriversLicenseRemoteUpload
    );
};

export const isCoBuyerDriversLicense = (internalDocumentTypeId: InternalDocumentTypeIds) => {
    return (
        internalDocumentTypeId === InternalDocumentTypeIds.CoBuyerDriversLicense ||
        internalDocumentTypeId === InternalDocumentTypeIds.CoBuyerDriversLicenseRemoteUpload
    );
};

export const getIdVerificationClass = (identificationResults: IdVerification): string => {
    if (!identificationResults) return null;
    switch (identificationResults.status) {
        case IdCheckStatus.Passed:
            // green
            return 'id-check-verified';
        case IdCheckStatus.Pending:
        case IdCheckStatus.FrontPassedBackFailed:
            // grey
            return 'id-check-pending';
        case IdCheckStatus.NameOnDealMismatch:
        case IdCheckStatus.PassedWithWarnings:
        case IdCheckStatus.FrontFailedBackPassed:
        case IdCheckStatus.FacialRecognitionFailed:
            // yellow
            return 'id-check-verified-warning';
        case IdCheckStatus.Failed:
        case IdCheckStatus.Expired:
        case IdCheckStatus.ParsedOkButInvalid:
            // red
            return 'id-check-unverified';
        case IdCheckStatus.ReadyForSubmit:
            // orange
            return 'id-check-notverified';
    }
};

export function AssigneeToDocStatus(assignee: Assignees): DocumentStatus | null {
    switch (assignee) {
        case Assignees.buyer:
            return DocumentStatus.BuyerQueue;
        case Assignees.cobuyer:
            return DocumentStatus.CoBuyerQueue;
        case Assignees.dealer:
            return DocumentStatus.DealerQueue;
    }

    return null;
}
